import { createStyles, Divider, Group, Stack, Text } from '@mantine/core';
import React, { Dispatch, SetStateAction } from 'react';
import { contextMenu, Menu } from 'react-contexify';

import {
  useDeleteSpace,
  useSpace,
  useUpdateSpace,
} from '@portals/api/organizations';
import { useConfirmationModal } from '@portals/framework';
import { ReactComponent as BoxAdd } from '@portals/icons/linear/box-add.svg';
import { ReactComponent as Edit } from '@portals/icons/linear/edit.svg';
import { ReactComponent as MonitorMobile } from '@portals/icons/linear/monitor-mobbile.svg';
import { ReactComponent as Snooze } from '@portals/icons/linear/snooze.svg';
import { ReactComponent as Trash } from '@portals/icons/linear/trash.svg';
import { useOpenModal } from '@portals/redux';
import { suppressPropagation } from '@portals/utils';

import { canEdit } from '../../../../lib/access';

interface NodeContextMenuProps {
  nodeId: number;
  onEditToggle: () => void;
  onCreateSpace: () => void;
  setIsContextMenuOpen: Dispatch<SetStateAction<boolean>>;
}

export function NodeContextMenu({
  nodeId,
  onEditToggle,
  onCreateSpace,
  setIsContextMenuOpen,
}: NodeContextMenuProps) {
  const openModal = useOpenModal();
  const { classes } = useStyles();
  const asyncConfirmationCheck = useConfirmationModal();

  const space = useSpace(nodeId);
  const updateSpace = useUpdateSpace();
  const deleteSpace = useDeleteSpace();

  if (!space || !canEdit(space)) return null;

  const canRemove = space.space_type !== 'root';
  const isUnderMaintenance = space.state?.maintenance || false;

  const handleDelete = async () => {
    const isConfirmed = await asyncConfirmationCheck({
      title: `Delete Space "${space.name}"`,
      description: `All contained information will be lost and all assigned devices will be moved to "Unsorted" space. Do you want to continue?`,
      confirmationLabel: 'Delete',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) {
      deleteSpace.mutate(space.id);
    } else {
      return;
    }
  };

  const handleAddDevice = () => openModal('AddDevice', space.id);
  const handleSetMaintenance = (enabled) =>
    updateSpace.mutate({
      spaceId: space.id,
      updatedSpace: { maintenance: enabled },
    });

  return (
    <Menu
      id={nodeId}
      onShown={() => setIsContextMenuOpen(true)}
      onHidden={() => setIsContextMenuOpen(false)}
    >
      <Stack className={classes.container} spacing="xs" pt="xs">
        <Text className={classes.title}>{space.name}</Text>

        <Divider color="gray.3" />

        <Group
          className={classes.item}
          spacing="xs"
          onClick={suppressPropagation(() => {
            handleSetMaintenance(!isUnderMaintenance);
            contextMenu.hideAll();
          })}
        >
          {isUnderMaintenance ? <Snooze /> : <Snooze />}
          Set maintenance {isUnderMaintenance ? 'OFF' : 'ON'}
        </Group>

        <Group
          className={classes.item}
          spacing="xs"
          onClick={suppressPropagation(() => {
            handleAddDevice();
            contextMenu.hideAll();
          })}
        >
          <MonitorMobile />
          Claim device
        </Group>

        <Divider color="gray.3" />

        <Group
          className={classes.item}
          spacing="xs"
          onClick={suppressPropagation(() => {
            onEditToggle();
            contextMenu.hideAll();
          })}
        >
          <Edit />
          Rename
        </Group>

        <Group
          className={classes.item}
          spacing="xs"
          onClick={() => {
            onCreateSpace();
            contextMenu.hideAll();
          }}
        >
          <BoxAdd />
          Add new Space
        </Group>

        {canRemove ? (
          <Group
            className={classes.item}
            spacing="xs"
            sx={(theme) => ({
              color: theme.colors.red_accent[4],
            })}
            onClick={suppressPropagation(() => {
              handleDelete();
              contextMenu.hideAll();
            })}
          >
            <Trash />
            Remove
          </Group>
        ) : null}
      </Stack>
    </Menu>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    zIndex: 999,
    padding: '0 4px',
    width: 224,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[9],
    fontWeight: 600,
    width: '100%',
    textAlign: 'center',
  },
  item: {
    flexShrink: 0,
    height: 33,
    padding: `0 ${theme.spacing.lg}`,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[9],
    fontWeight: 300,
    transition: 'all 0.15s ease-in-out',
    borderRadius: theme.radius.sm,
    cursor: 'pointer',

    svg: {
      height: 18,
      width: 18,
    },

    ':hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
}));
